import "./Home.css";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  FaArrowLeft,
  FaBed,
  FaBone,
  FaCheck,
  FaMoon,
  FaShieldAlt,
  FaMagic,
} from "react-icons/fa";
import pillow from "../../assets/images/Cervica-pillow.png";
import { PILLOW_COPY, PILLOW_NAME } from "../../content/pillowContent";

const reveal = {
  hidden: { opacity: 0, y: 34 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

function Home() {
  const { scrollYProgress } = useScroll();

  const heroY = useTransform(scrollYProgress, [0, 0.22], [0, 90]);

  const heroRotate = useTransform(scrollYProgress, [0, 0.22], [-2, 3]);

  return (
    <main className="home-v2">
      <section className="hero-v2">
        <div className="hero-grid" aria-hidden="true" />
        <div className="hero-orb hero-orb-one" aria-hidden="true" />
        <div className="hero-orb hero-orb-two" aria-hidden="true" />
        <div className="hero-noise" aria-hidden="true" />

        <motion.div
          className="hero-copy"
          initial="hidden"
          animate="visible"
          variants={reveal}
        >
          <motion.div
            className="eyebrow-pill"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{
              delay: 0.1,
              duration: 0.5,
            }}
          >
            <span className="eyebrow-dot" />
            <FaMoon />
            שינה עמוקה ורגועה יותר
          </motion.div>

          <h1 className="hero-title">
            <span className="hero-title-line">כרית</span>

            <span className="hero-title-line hero-title-accent">ארגונומית</span>

            <span className="hero-title-line hero-title-outline">CERVICA</span>
          </h1>

          <p className="hero-lead">
            {PILLOW_COPY.hook}
          </p>

          <div className="hero-cta-row">
            <Link to="/product" className="btn-v2 btn-v2-primary">
              <span>לרכישה עכשיו</span>
              <FaArrowLeft />
            </Link>

            <Link to="/doctor" className="btn-v2 btn-v2-glass">
              הסיפור של CERVICA
            </Link>
          </div>

          <div className="hero-proof">
            <div className="proof-item">
              <strong>01</strong>
              <span>תמיכה בצוואר ובכתפיים</span>
            </div>

            <div className="proof-divider" />

            <div className="proof-item">
              <strong>02</strong>
              <span>נוחות לאורך כל הלילה</span>
            </div>

            <div className="proof-divider" />

            <div className="proof-item">
              <strong>03</strong>
              <span>חומר ויסקו איכותי</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          className="hero-product-stage"
          style={{
            y: heroY,
            rotate: heroRotate,
          }}
          initial={{
            opacity: 0,
            scale: 0.88,
          }}
          animate={{
            opacity: 1,
            scale: 1,
          }}
          transition={{
            duration: 1,
            delay: 0.12,
            ease: [0.16, 1, 0.3, 1],
          }}
        >
          <div className="product-halo" aria-hidden="true" />

          <div className="product-ring product-ring-a" aria-hidden="true" />

          <div className="product-ring product-ring-b" aria-hidden="true" />

          <img src={pillow} alt={`${PILLOW_NAME} Cervica`} className="hero-pillow" />

          <motion.div
            className="floating-spec floating-spec-top"
            animate={{ y: [0, -8, 0] }}
            transition={{
              duration: 4.3,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            <span className="spec-icon">
              <FaBone />
            </span>

            <div>
              <small>מבנה ארגונומי</small>
              <strong>תמיכה לצוואר</strong>
            </div>
          </motion.div>

          <motion.div
            className="floating-spec floating-spec-bottom"
            animate={{ y: [0, 9, 0] }}
            transition={{
              duration: 5.2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            <span className="spec-icon">
              <FaMoon />
            </span>

            <div>
              <small>לאורך כל הלילה</small>
              <strong>שינה רגועה יותר</strong>
            </div>
          </motion.div>

          <div className="product-label">
            <span>CERVICA</span>
            <small>{PILLOW_NAME}</small>
          </div>
        </motion.div>

        <div className="hero-scroll-cue" aria-hidden="true">
          <span>גלילה</span>
          <i />
        </div>
      </section>

      <section className="ticker" aria-label="היתרונות של CERVICA">
        <div className="ticker-track">
          <span>תמיכה ארגונומית</span>
          <i>✦</i>

          <span>חומר ויסקו איכותי</span>
          <i>✦</i>

          <span>שינה טובה יותר</span>
          <i>✦</i>

          <span>לשינה על הגב ועל הצד</span>
          <i>✦</i>

          <span>תמיכה ארגונומית</span>
          <i>✦</i>

          <span>חומר ויסקו איכותי</span>
          <i>✦</i>

          <span>שינה טובה יותר</span>
          <i>✦</i>

          <span>לשינה על הגב ועל הצד</span>
          <i>✦</i>
        </div>
      </section>

      <section className="statement-section">
        <motion.div
          className="statement-copy"
          initial="hidden"
          whileInView="visible"
          viewport={{
            once: true,
            amount: 0.35,
          }}
          variants={reveal}
        >
          <span className="mini-kicker">למה CERVICA?</span>

          <h2>
            שינה טובה מתחילה
            <span>בתמיכה הנכונה.</span>
          </h2>

          <p>
            {PILLOW_COPY.intro}
          </p>
        </motion.div>

        <div className="statement-watermark" aria-hidden="true">
          CERVICA
        </div>
      </section>

      <section className="feature-stage">
        <div className="feature-stage-heading">
          <span className="mini-kicker dark">נבנתה לשינה טובה יותר</span>

          <h2>ארבעה יתרונות לשינה עמוקה ורגועה יותר</h2>
        </div>

        <div className="feature-bento">
          <motion.article
            className="bento-card bento-card-large"
            initial={{
              opacity: 0,
              y: 24,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
              amount: 0.2,
            }}
            transition={{ duration: 0.55 }}
          >
            <div className="bento-number">01</div>

            <div className="bento-icon">
              <FaBone />
            </div>

            <h3>עיצוב גלי ייחודי</h3>

            <p>
              העיצוב הגלי שומר על יישור נכון של עמוד השדרה לאורך הלילה.
            </p>

            <div className="bento-visual neck-visual">
              <div className="neck-line neck-line-one" />
              <div className="neck-line neck-line-two" />
              <div className="neck-dot" />
            </div>
          </motion.article>

          <motion.article
            className="bento-card"
            initial={{
              opacity: 0,
              y: 24,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
              amount: 0.2,
            }}
            transition={{
              duration: 0.55,
              delay: 0.08,
            }}
          >
            <div className="bento-number">02</div>

            <div className="bento-icon">
              <FaMoon />
            </div>

            <h3>תמיכה בצוואר ובכתפיים</h3>

            <p>נועדה לשחרר מתח ולפזר לחץ באזור הצוואר והכתפיים לאורך כל הלילה.</p>

            <div className="moon-orbit">
              <span />
            </div>
          </motion.article>

          <motion.article
            className="bento-card"
            initial={{
              opacity: 0,
              y: 24,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
              amount: 0.2,
            }}
            transition={{
              duration: 0.55,
              delay: 0.14,
            }}
          >
            <div className="bento-number">03</div>

            <div className="bento-icon">
              <FaBed />
            </div>

            <h3>חומר ויסקו איכותי</h3>

            <p>מתאים את עצמו לצורת הצוואר והראש, לתמיכה יציבה ונעימה.</p>

            <div className="foam-layers">
              <i />
              <i />
              <i />
            </div>
          </motion.article>

          <motion.article
            className="bento-card bento-card-wide"
            initial={{
              opacity: 0,
              y: 24,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
              amount: 0.2,
            }}
            transition={{
              duration: 0.55,
              delay: 0.18,
            }}
          >
            <div className="bento-number">04</div>

            <div className="bento-icon">
              <FaShieldAlt />
            </div>

            <h3>לשינה על הגב ועל הצד</h3>

            <p>
              המבנה הארגונומי מותאם לשינה על הגב ועל הצד, עם תמיכה יציבה ונעימה.
            </p>

            <div className="quality-chip">
              <FaMagic />
              נוחות באיכות גבוהה
            </div>
          </motion.article>
        </div>
      </section>

      <section className="product-story">
        <div className="story-media">
          <div className="story-grid" aria-hidden="true" />

          <motion.img
            src={pillow}
            alt={`פרטי ${PILLOW_NAME} Cervica`}
            initial={{
              opacity: 0,
              x: 45,
              rotate: 2,
            }}
            whileInView={{
              opacity: 1,
              x: 0,
              rotate: -2,
            }}
            viewport={{
              once: true,
              amount: 0.3,
            }}
            transition={{ duration: 0.75 }}
          />

          <span className="story-index">פרט 01</span>
        </div>

        <motion.div
          className="story-content"
          initial={{
            opacity: 0,
            y: 34,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
            amount: 0.35,
          }}
          transition={{ duration: 0.7 }}
        >
          <span className="mini-kicker dark">עוצבה סביבכם</span>

          <h2>עיצוב גלי, תמיכה יציבה ונעימה.</h2>

          <p>
            {PILLOW_COPY.shape}
          </p>

          <p>
            {PILLOW_COPY.material}
          </p>

          <div className="story-checks">
            <div>
              <FaCheck />
              תמיכה לראש ולצוואר
            </div>

            <div>
              <FaCheck />
              מתאימה לשינה על הגב ועל הצד
            </div>

            <div>
              <FaCheck />
              מבנה ארגונומי ונוח
            </div>

            <div>
              <FaCheck />
              חומר ויסקו איכותי
            </div>
          </div>

          <Link to="/product" className="text-link">
            לצפייה במוצר
            <FaArrowLeft />
          </Link>
        </motion.div>
      </section>

      <section className="final-cta">
        <div className="final-cta-glow" aria-hidden="true" />

        <motion.div
          initial={{
            opacity: 0,
            y: 30,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
            amount: 0.45,
          }}
          transition={{ duration: 0.65 }}
        >
          <span className="mini-kicker">מוכנים לשינה טובה יותר?</span>

          <h2>{PILLOW_COPY.cta}</h2>

          <p>
            {PILLOW_COPY.reviews}
          </p>

          <p>
            {PILLOW_COPY.shipping}
          </p>

          <Link to="/product" className="btn-v2 btn-v2-light">
            לרכישה
            <FaArrowLeft />
          </Link>
        </motion.div>
      </section>
    </main>
  );
}

export default Home;
